import { useCallback } from 'react';

import {
	useSelectedStore,
	useToastContext,
	useWishlistContext,
} from 'contexts';
import { fetchDpApiEndpointResult } from 'endpoints/dpApi';
import type { BasketRowResponse, Wishlist } from 'models/api';
import type { VariantId } from 'models/product';
import { filterTruthy } from 'utils/collection';
import { asArray, is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { parseMarkdown } from 'utils/string';

import { usePath } from './navigation';
import { useGlobalLinks } from './useSitecoreContext';

interface VariantData {
	quantity?: number;
	variantId: VariantId;
}

interface OptimisticRowPickedUpdate {
	picked: boolean;
	variantId: VariantId;
}
interface OptimisticRowQuantityUpdate {
	quantity: number;
	variantId: VariantId;
}

function mergeBasketRows<T extends { variantId: VariantId }>(
	wishlist: Wishlist,
	rows: T[],
	getRowUpdate: (row: T) => Partial<BasketRowResponse>,
): Wishlist {
	if (wishlist.basketRows.length === 0 || rows.length === 0) {
		return wishlist;
	}
	return {
		...wishlist,
		basketRows: wishlist.basketRows.map((basketRow) => {
			const row = rows.find((r) => r.variantId === basketRow.variantId);
			return row ? { ...basketRow, ...getRowUpdate(row) } : basketRow;
		}),
	};
}

function mergeBasketRowsPicked(
	wishlist: Wishlist,
	rows: OptimisticRowPickedUpdate[],
): Wishlist {
	return mergeBasketRows(wishlist, rows, (row) => ({ picked: row.picked }));
}

function mergeBasketRowsQuantity(
	wishlist: Wishlist,
	rows: OptimisticRowQuantityUpdate[],
): Wishlist {
	return mergeBasketRows(wishlist, rows, (row) => ({ quantity: row.quantity }));
}

export function useWishlist() {
	const { t } = useI18n();
	const path = usePath();
	const { wishlist: wishlistPagePath } = useGlobalLinks();
	const { sendToast } = useToastContext();
	const { selectedStore } = useSelectedStore();
	const {
		clearWishlist,
		openAddedProductPopover,
		updateTotalQuantity,
		updateWishlist,
		wishlist,
	} = useWishlistContext();

	const updateVariantFull = useCallback(
		async (
			variantsData: VariantData | VariantData[],
			options: {
				setDistinctValue?: boolean;
			},
		) => {
			const res = await fetchDpApiEndpointResult({
				name: 'PostWishlistVariants',
				body: asArray(variantsData).map((variantData) => ({
					quantity: variantData.quantity ?? 1,
					...variantData,
				})),
				queryParams: {
					setDistinctValue: options.setDistinctValue ? 'true' : 'false',
					storeId: selectedStore?.id,
				},
			});

			if (!res.isSuccess()) {
				sendToast({
					message: t('general_critical_error_text'),
					variant: 'error',
				});
				throw new Error('Update variant full error');
			}
			return res.value;
		},
		[selectedStore?.id, sendToast, t],
	);

	const updateVariantMini = useCallback(
		async (
			variantsData: VariantData | VariantData[],
			options: {
				returnAccessories: boolean;
				returnAddedProducts: boolean;
				setDistinctValue: boolean;
				showPopover: boolean;
				showToast: boolean;
			},
		) => {
			const variants = asArray(variantsData);

			const res = await fetchDpApiEndpointResult({
				name: 'PostWishlistMiniVariants',
				body: variants.map((variant) => ({
					variantId: variant.variantId,
					quantity: variant.quantity ?? 1,
				})),
				queryParams: {
					returnAddedProducts: options.returnAddedProducts ? 'true' : 'false',
					returnAccessories: options.returnAccessories ? 'true' : 'false',
					setDistinctValue: options.setDistinctValue ? 'true' : 'false',
				},
			});

			if (!res.isSuccess()) {
				sendToast({
					message: t('general_critical_error_text'),
					variant: 'error',
				});
				throw new Error('Update variant mini error');
			}

			const isSuccess = res.value.success;

			if (options.showToast) {
				const message = is.arrayWithLength(res.value.addedProducts)
					? parseMarkdown(
							t('variant_added_to_wishlist_text', {
								productTitle: res.value.addedProducts
									.map(({ title }) => title)
									.join(', '),
							}),
						)
					: res.value.message;

				if (message) {
					sendToast({
						message,
						variant: isSuccess ? 'success' : 'error',
						link: wishlistPagePath,
						linkText: t('continue_to_wishlist'),
					});
				}
			}

			if (!isSuccess) {
				throw new Error('Failed to add product to wishlist');
			}

			if (options.showPopover) {
				openAddedProductPopover(res.value);
			}

			return res.value;
		},
		[openAddedProductPopover, sendToast, t, wishlistPagePath],
	);

	const addToWishlist = useCallback(
		async (
			variantsData: VariantData | VariantData[],
			{
				optimisticQuantityUpdate = false,
				returnAccessories = false,
				returnAddedProducts = false,
				setDistinctValue = false,
				showPopover = false,
				showToast = false,
			}: {
				optimisticQuantityUpdate?: boolean;
				returnAccessories?: boolean;
				returnAddedProducts?: boolean;
				setDistinctValue?: boolean;
				showPopover?: boolean;
				showToast?: boolean;
			},
		): Promise<void> => {
			if (optimisticQuantityUpdate && wishlist) {
				updateWishlist(
					mergeBasketRowsQuantity(
						wishlist,
						filterTruthy(asArray(variantsData), 'quantity'),
					),
				);
			}
			// Differs from useCart, since shared wishlists are on the /wishlist/{id}
			// route, and the shared wishlist only needs to do mini updates, as it's
			// immutable so don't want to match that with path.startsWith.
			if (path.is(wishlistPagePath)) {
				const newWishlist = await updateVariantFull(variantsData, {
					setDistinctValue,
				});
				updateWishlist(newWishlist);
				updateTotalQuantity(newWishlist.totalQty);
			} else {
				const newMiniWishlist = await updateVariantMini(variantsData, {
					setDistinctValue,
					showToast,
					showPopover,
					returnAddedProducts,
					returnAccessories,
				});
				updateTotalQuantity(newMiniWishlist.totalQty);
			}
		},
		[
			path,
			updateTotalQuantity,
			updateVariantFull,
			updateVariantMini,
			updateWishlist,
			wishlist,
			wishlistPagePath,
		],
	);

	const removeFromWishlist = useCallback(
		async (variantId: VariantId) => {
			const res = await fetchDpApiEndpointResult({
				name: 'DeleteWishlistVariant',
				pathParams: { variantId },
				queryParams: { storeId: selectedStore?.id },
			});

			if (!res.isSuccess()) {
				sendToast({
					message: t('general_critical_error_text'),
					variant: 'error',
				});
				throw new Error('Remove from wishlist error');
			}

			updateWishlist(res.value);
			updateTotalQuantity(res.value.totalQty);
		},
		[selectedStore?.id, sendToast, t, updateTotalQuantity, updateWishlist],
	);

	const deleteWishlist = useCallback(async () => {
		const res = await fetchDpApiEndpointResult('DeleteWishlist');

		if (res.isFailure()) {
			sendToast({
				message: t('general_critical_error_text'),
				variant: 'error',
			});
			throw new Error('Delete wishlist error');
		}

		clearWishlist();
	}, [clearWishlist, sendToast, t]);

	const setVariantPicked = useCallback(
		async (variantId: VariantId, picked: boolean) => {
			if (wishlist) {
				updateWishlist(
					mergeBasketRowsPicked(wishlist, [{ variantId, picked }]),
				);
			}
			const res = await fetchDpApiEndpointResult({
				name: 'PutWishlistVariantPicked',
				pathParams: { variantId },
				queryParams: { picked: picked ? 'true' : 'false' },
			});

			if (res.isFailure()) {
				// Restore previous state.
				// NOTE! This assumes there is no other concurrent updates to the
				// wishlist. Since the closure will restore to the state before the
				// request, any optimistic updates made after the request has started
				// will be lost.
				if (wishlist) {
					updateWishlist(
						mergeBasketRowsPicked(wishlist, [{ variantId, picked: !picked }]),
					);
				}
				sendToast({
					message: t('general_critical_error_text'),
					variant: 'error',
				});
				throw new Error('Set picked error');
			}
		},
		[sendToast, t, updateWishlist, wishlist],
	);

	const removeAllPicked = useCallback(async () => {
		const pickedRows = wishlist?.basketRows.filter((row) => row.picked) ?? [];
		if (pickedRows.length === 0) {
			return;
		}
		const newWishlist = await updateVariantFull(
			pickedRows.map((row) => ({ variantId: row.variantId, quantity: 0 })),
			{ setDistinctValue: true },
		);
		updateWishlist(newWishlist);
		updateTotalQuantity(newWishlist.totalQty);
	}, [
		updateTotalQuantity,
		wishlist?.basketRows,
		updateVariantFull,
		updateWishlist,
	]);

	return {
		addToWishlist,
		removeFromWishlist,
		deleteWishlist,
		removeAllPicked,
		setVariantPicked,
	};
}
